import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import {
  getDatasources,
  createDatasources,
  updateDatasources,
  deleteDatasources,
  bulkReorderDatasources,
  type DataSourceCreate,
  type DataSourceUpdate,
} from '@/services/apiClient';
import { AgentProxyService } from '@/services/agentProxyService';
import { useMemo } from 'react';
import { useDataSourceFromStore } from './useDataSourceFromStore';
import { useDataSourceStore } from '@/stores/dataSourceStore';
// Import { invalidateFetchUrl } from '@/utils/fetchDeduplication';

// Note: DedupDataSourceService removed - using centralized dataSourceStore instead

// Query keys
export const dataSourceKeys = {
  all: ['datasources'] as const,
  lists: () => [...dataSourceKeys.all, 'list'] as const,
  list: (filters: string) => [...dataSourceKeys.lists(), { filters }] as const,
  details: () => [...dataSourceKeys.all, 'detail'] as const,
  detail: (id: string) => [...dataSourceKeys.details(), id] as const,
  infinite: () => [...dataSourceKeys.all, 'infinite'] as const,
};

// Get all data sources (traditional query)
export const useDataSourcesQuery = () => {
  return useQuery({
    queryKey: dataSourceKeys.lists(),
    queryFn: async () => {
      const { data: response } = await getDatasources({
        throwOnError: true
      });
      return response.data || [];
    },
    enabled: (() => {
      // Only fetch if user is authenticated
      const token = localStorage.getItem('token')
      const userStr = localStorage.getItem('user')
      return !!(token && userStr)
    })(),
    // 5 minutes - list doesn't change frequently
    staleTime: 5 * 60 * 1000,
    // 15 minutes - keep in cache
    gcTime: 15 * 60 * 1000,
    // Don't refetch on window focus
    refetchOnWindowFocus: false,
    // Don't refetch on component mount if data exists
    refetchOnMount: false,
  });
};

// Get data sources with infinite pagination
export const useInfiniteDataSourcesQuery = (limit = 20) => {
  return useInfiniteQuery({
    queryKey: dataSourceKeys.infinite(),
    queryFn: async ({ pageParam = 1 }) => {
      const { data: response } = await getDatasources({
        query: {
          page: pageParam,
          limit,
        },
        throwOnError: true
      });
      return {
        data: response.data || [],
        pagination: response.pagination,
        nextPage: response.pagination?.has_next ? pageParam + 1 : undefined,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    // 1 second stale time to prevent duplicate initial requests
    staleTime: 1000,
    // 15 minutes - keep in cache
    gcTime: 15 * 60 * 1000,
    // Don't refetch on window focus
    refetchOnWindowFocus: false,
    // Don't auto-refetch on mount to prevent duplicates
    refetchOnMount: false,
    enabled: (() => {
      // Only fetch if user is authenticated
      const token = localStorage.getItem('token')
      const userStr = localStorage.getItem('user')
      return !!(token && userStr)
    })(),
  });
};

// Get single data source using centralized store to prevent duplicate API calls
export const useDataSourceQuery = (id: string) => {
  // Normalize the ID to prevent unnecessary re-queries
  const normalizedId = useMemo(() => id.trim() || '', [id]);
  
  // Use the centralized store hook
  return useDataSourceFromStore(normalizedId);
};

// Create data source
export const useCreateDataSourceMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (dataSource: DataSourceCreate) => {
      const { data } = await createDatasources({
        body: dataSource,
        throwOnError: true
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.infinite() });
    },
  });
};

// Update data source
export const useUpdateDataSourceMutation = () => {
  const queryClient = useQueryClient();
  const clearDataSource = useDataSourceStore((state) => state.clearDataSource);
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: DataSourceUpdate }) => {
      const { data: responseData } = await updateDatasources({
        path: { datasource_id: id },
        body: data,
        throwOnError: true
      });
      return responseData;
    },
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.infinite() });
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.detail(id) });
      // Clear from centralized store
      clearDataSource(id);
    },
  });
};

// Delete data source
export const useDeleteDataSourceMutation = () => {
  const queryClient = useQueryClient();
  const clearDataSource = useDataSourceStore((state) => state.clearDataSource);
  
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteDatasources({
        path: { datasource_id: id },
        throwOnError: true
      });
      return id;
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.infinite() });
      queryClient.removeQueries({ queryKey: dataSourceKeys.detail(id) });
      // Clear from centralized store
      clearDataSource(id);
    },
  });
};

// Test data source connection
interface TestConnectionParams {
  datasourceId: string;
  agentId?: string;
}

export const useTestDataSourceMutation = () => {
  return useMutation({
    mutationFn: async ({ datasourceId, agentId }: TestConnectionParams) => {
      return AgentProxyService.testConnection(datasourceId, agentId);
    },
  });
};

// Bulk reorder datasources (with optimistic update for smooth drag-and-drop animation)
export const useBulkReorderDataSourcesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      const { data } = await bulkReorderDatasources({
        body: { orderedIds },
        throwOnError: true
      });
      return data;
    },
    onMutate: async (orderedIds) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: dataSourceKeys.infinite() });

      // Snapshot the previous value for rollback
      const previousInfinite = queryClient.getQueryData(dataSourceKeys.infinite());

      // Optimistically update the order in the infinite query cache
      queryClient.setQueryData(dataSourceKeys.infinite(), (old: unknown) => {
        const data = old as { pages: { data: { id?: string; order?: number }[]; pagination?: unknown; nextPage?: number }[]; pageParams: unknown[] } | undefined;
        if (!data?.pages) return old;

        return {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            data: page.data.map((ds) => {
              const newOrder = orderedIds.indexOf(ds.id || '');
              if (newOrder !== -1) {
                return { ...ds, order: newOrder };
              }
              return ds;
            }),
          })),
        };
      });

      return { previousInfinite };
    },
    onError: (_err, _orderedIds, context) => {
      // Rollback to the previous value on error
      if (context?.previousInfinite) {
        queryClient.setQueryData(dataSourceKeys.infinite(), context.previousInfinite);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.infinite() });
    },
  });
};
